// useMe — single-flight current-user fetch using @preact/signals.
// On 401 the hook resolves to null; routes can then redirect to /login.

import { signal, type Signal } from "@preact/signals";
import { useEffect } from "preact/hooks";
import { api, ApiError } from "./api";
import { setTeamsBearer } from "./teamsContext";
import { clearDesignChoice, syncDesignFromServer } from "./theme";
import { syncUxFlagsFromServer, type UxFlagLevel } from "./ux";
import type { UiDesign } from "@juuplan/shared";

export interface Me {
  id: number;
  name: string;
  email: string;
  avatar_url: string | null;
  is_admin: boolean;
  /** Sprint 3 Story 4: externe Gäste sind UI-mäßig auf 1 Projekt beschränkt. */
  is_external?: boolean;
  /** True when the admin has enabled AI (ai_settings.enabled = true). Optional so old server responses remain valid. */
  ai_enabled?: boolean;
  /** Design-Schalter pro Benutzer (user_preferences.ui_design). Optional, damit
   *  aeltere Server-Antworten gueltig bleiben — fehlt er, gilt „classic". */
  ui_design?: UiDesign;
  /** UX-Vorschlaege: aufgeloeste Flag-Stufen (siehe lib/ux.ts). Optional. */
  ux_flags?: Record<string, UxFlagLevel>;
}

export interface MeState {
  loading: boolean;
  user: Me | null;
  error: string | null;
}

const initial: MeState = { loading: true, user: null, error: null };
const meSignal: Signal<MeState> = signal(initial);
let inFlight: Promise<void> | null = null;

async function loadMe(): Promise<void> {
  try {
    const user = await api<Me>("/api/me", { silent401: true });
    meSignal.value = { loading: false, user, error: null };
    // Serverwert gilt; Cache wird an diese User-ID gebunden.
    syncDesignFromServer(user);
    syncUxFlagsFromServer(user);
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      meSignal.value = { loading: false, user: null, error: null };
      // Nicht angemeldet: kein Design eines frueheren Benutzers stehen lassen.
      syncDesignFromServer(null);
      syncUxFlagsFromServer(null);
      return;
    }
    const message = err instanceof Error ? err.message : "Unknown error";
    meSignal.value = { loading: false, user: null, error: message };
  }
}

export function useMe(): MeState {
  useEffect(() => {
    if (!inFlight && meSignal.value.loading) {
      inFlight = loadMe();
    }
  }, []);
  return meSignal.value;
}

/** Nach erfolgreichem PATCH den lokalen /api/me-Stand nachziehen, damit eine
 *  spaetere Revalidierung nichts zurueckdreht. */
export function setMeDesign(design: UiDesign): void {
  const user = meSignal.value.user;
  if (!user) return;
  meSignal.value = { ...meSignal.value, user: { ...user, ui_design: design } };
}

export function refreshMe(): Promise<void> {
  inFlight = loadMe();
  return inFlight;
}

// --- tab-focus revalidation ---

let lastRevalidateAt = 0;
const REVALIDATE_DEBOUNCE_MS = 3000;

/** Revalidates /api/me on tab focus/visibility. Exported for unit-testing. */
export function revalidateMe(): void {
  if (meSignal.value.loading) return;        // initial load still running
  if (meSignal.value.user === null) return;  // not logged in — do not auto-refetch
  const now = Date.now();
  if (now - lastRevalidateAt < REVALIDATE_DEBOUNCE_MS) return;
  lastRevalidateAt = now;
  void refreshMe();
}

if (typeof window !== "undefined") {
  window.addEventListener("focus", revalidateMe);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") revalidateMe();
  });
}

export async function logout(): Promise<void> {
  try {
    await api("/api/auth/logout", { method: "POST" });
  } finally {
    // Sprint 3 QA H1: alle juuplan-Auth-Reste aus localStorage räumen,
    // sonst loggt der nächste Page-Load den User über den persistierten
    // Bearer-Token wieder ein. Wir trotz setTeamsBearer(null) noch explizit
    // den User-Prefs-Cache leeren.
    setTeamsBearer(null);
    try {
      localStorage.removeItem("juuplan:teams_bearer");
    } catch {
      // ignore
    }
    clearDesignChoice();
    meSignal.value = { loading: false, user: null, error: null };
    window.location.href = "/login";
  }
}
